import { useState } from 'react';
import {
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  GridColDef,
  GridFilterPanel,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import { capitalizeFirstLetter } from 'helpers';
import {
  AllClientsDataGridColumns,
  DigitalIdentityTrustStatus,
  Estate,
  PortalMembership,
  PortalMembership_AllClientsDataGridPageFilter,
  useAuth_ImpersonateMutation,
  useEquifax_GetDigitalIdentityTrustLazyQuery,
  usePortalMembership_GetAllPortalMembershipsForAllClientsDataGridLazyQuery,
  User,
  useUser_UpdateUserMutation,
} from 'queries';
import { MoreVertical } from 'ts-react-feather-icons';

import AuditModal from 'common/audit-modal';
import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import DitStatusLabel from 'pages/common/client/dit-status-label';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  usersContainer: {
    marginTop: '11px',
    width: '100%',
    background: '#FFFFFF',
    justifyContent: 'center',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    borderRadius: '2px',
    overflow: 'auto',
    height: '600px',
  },
  nameWrapper: {
    display: 'block',
    lineHeight: '1.43',
  },
  secondaryLabel: {
    display: 'block',
    lineHeight: '1.43',
    fontSize: '.75rem',
    color: '#999',
  },
  title: {
    fontWeight: 600,
    paddingTop: '10px',
  },
  listAction: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterPanelWrapper: {
    position: 'absolute',
    top: '-60px',
    backgroundColor: '#F1FCFF',
  },
  link: {
    paddingTop: '4px',
    paddingRight: '10px',
    cursor: 'pointer',
  },
  popoverContent: {
    padding: 15,
  },
}));

export default function AllClients() {
  const classes = useStyles();

  const [currentClient, setCurrentClient] = useState<User | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<User>();
  const [showAuditModal, setShowAuditModal] = useState<boolean>(false);
  const [auditEntityId, setAuditEntityId] = useState<string>();
  const [userEstatesDetailAnchorEl, setUserEstatesDetailAnchorEl] = useState<
    undefined | HTMLElement
  >();

  const [impersonate] = useAuth_ImpersonateMutation({
    onCompleted: () => {
      // react history.push() doesn't work because a page reload is needed for impersonate to take effect
      // TODO: Remove the need of a page refresh for impersonate to take effect.
      window.location.href = `/home/${selectedClient!.defaultPortal.id}/home`;
    },
    onError: (e) => {
      showAlert({
        message: 'Error impersonating user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [
    getAllPortalMembershipsForAllClientsDataGridLazyQuery,
    getAllPortalMembershipsForAllClientsDataGridLazyQueryResult,
  ] = usePortalMembership_GetAllPortalMembershipsForAllClientsDataGridLazyQuery({
    variables: {
      input: {
        paginationInput: {
          page: defaultPaginationModel.page,
          pageSize: defaultPaginationModel.pageSize,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting all users: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
  });

  const [updateUser] = useUser_UpdateUserMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [loadDigitalIdentityTrust] = useEquifax_GetDigitalIdentityTrustLazyQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting digital identity trust: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleIconClick = (event: any, user: User) => {
    setSelectedClient(user);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = () => {
    setAnchorEl(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUserEstatesDetailClick = (event: any) => {
    setUserEstatesDetailAnchorEl(event.currentTarget);
  };
  const handleUserEstatesDetailClose = () => {
    setUserEstatesDetailAnchorEl(undefined);
  };

  const handleImpersonation = async () => {
    await impersonate({
      variables: { userId: selectedClient!.id },
    });
  };

  const handleRequestEfxDit = async () => {
    if (selectedClient!.efxDigitalIdentityTrust !== DigitalIdentityTrustStatus.Approve) {
      await tryLoadDigitalIdentityTrust();
    }
  };

  async function tryLoadDigitalIdentityTrust() {
    try {
      const { data: ditResultData } = await loadDigitalIdentityTrust({
        variables: {
          input: {
            firstName: selectedClient!.firstName,
            lastName: selectedClient!.lastName,
            phoneNumber: selectedClient!.phone,
            email: selectedClient!.email,
          },
        },
      });

      if (ditResultData && ditResultData.data.ditDataJson) {
        saveDITResponse(JSON.parse(ditResultData.data.ditDataJson));
      } else {
        // This block is executed when EFX != 200
        console.log(
          `UI:tryLoadDigitalIdentityTrust (user id ${selectedClient!.id}): DIT did not return data`,
        );
        // This will result in an response.decision = undefined and default to "Deny"
        saveDITResponse('Deny');
      }
    } catch (e) {
      console.log(
        `UI:tryLoadDigitalIdentityTrust (user id ${selectedClient!.id}) - DIT error: ${e}`,
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const saveDITResponse = (response: any) => {
    updateUser({
      variables: {
        input: {
          id: selectedClient!.id,
          efxDigitalIdentityTrust: parseDitDecision(response.decision),
          efxDitTransactionId: response?.transactionId,
          isConcierge: selectedClient!.isConcierge ? true : false,
          isSuperAdmin: selectedClient!.isSuperAdmin ? true : false,
        },
      },
    }).catch((e: Error) => {
      console.log(`UI:saveDITResponse (user id ${selectedClient!.id}) - update user error: ${e}`);
    });
  };

  const parseDitDecision = (decision: string) => {
    switch (decision) {
      case 'Approve':
        return DigitalIdentityTrustStatus.Approve;
      case 'Review':
        return DigitalIdentityTrustStatus.Review;
      case 'Deny':
        return DigitalIdentityTrustStatus.Deny;
      default:
        console.log(
          `UI:parseDitDecision (user id ${
            selectedClient!.id
          }) - Error parsing DIT decision: ${decision}`,
        );
        return DigitalIdentityTrustStatus.Deny;
    }
  };

  const columns: GridColDef<PortalMembership>[] = [
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.FullName,
      flex: 6,
      headerName: 'Name',
      renderCell: ({ row }) => {
        return (
          <div className={classes.nameWrapper}>
            <div>
              {row.user.firstName} {row.user.lastName}
            </div>
            <div className={classes.secondaryLabel}>{row.user.email}</div>
          </div>
        );
      },
      valueGetter: ({ row }) => {
        return `${row.user.firstName} ${row.user.lastName}`;
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.Email,
      flex: 6,
      headerName: 'Email',
      renderCell: ({ row }) => {
        return <div>{row.user.email}</div>;
      },
      valueGetter: ({ row }) => {
        return `${row.user.email}`;
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.CreatedAt,
      filterable: false,
      flex: 5,
      headerName: 'Joined',
      renderCell: ({ row }) => {
        return format(Date.parse(row.user.createdAt.toString()), 'h:mmaaa LLL d, y');
      },
      valueGetter: ({ row }) => {
        return row.user.createdAt;
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.FirmName,
      flex: 6,
      headerName: 'Firm Name',
      renderCell: ({ row }) => {
        const client = row.user as User;
        const firmName = client.defaultPortal.name;
        return firmName || '';
      },
      valueGetter: ({ row }) => {
        const client = row.user as User;
        const firmName = client.defaultPortal.name;
        return firmName || '';
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.Estates,
      flex: 2,
      filterable: false,
      sortable: false,
      headerName: 'Estates',
      renderCell: ({ row }) => {
        const client = row.user as User;
        const estates = client.defaultPortal.estates;
        if (estates && estates.length > 0) {
          return (
            <>
              <Link
                id={client.id}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick={(event: any) => {
                  setCurrentClient(client);
                  handleUserEstatesDetailClick(event);
                }}
                className={classes.link}
              >
                {estates.length}
              </Link>
            </>
          );
        }
      },
      valueGetter: ({ row }) => {
        return row.portal?.estates?.length || 0;
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.Permission,
      flex: 3,
      filterable: false,
      sortable: true,
      headerName: 'Permission',
      renderCell: ({ row }) => {
        const isPortalOwner = row.isPortalOwner;
        const permission = row.permission;
        return isPortalOwner ? 'OWNER' : permission;
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.AccountDisabled,
      flex: 2,
      filterable: false,
      sortable: true,
      headerName: 'Status',
      renderCell: ({ row }) => {
        const accountDisabled = row.user.accountDisabled;
        return accountDisabled ? 'Disabled' : 'Active';
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.FeatureSubscriptions,
      flex: 3,
      filterable: false,
      sortable: true,
      headerName: 'Subscription',
      renderCell: ({ row }) => {
        const client = row.user as User;
        const subscriptions =
          client.defaultPortal?.featureSubscriptions?.[0]?.featureSubscriptionStatus ?? '';
        const label = capitalizeFirstLetter(subscriptions);
        return <>{label}</>;
      },
    },
    {
      disableColumnMenu: true,
      field: AllClientsDataGridColumns.Dit,
      filterable: false,
      flex: 5,
      headerName: 'DIT',
      renderCell: ({ row }) => {
        const client = row.user as User;

        return (
          <>
            <DitStatusLabel efxDigitalIdentityTrust={client.efxDigitalIdentityTrust || undefined} />
            <List style={{ position: 'absolute', right: '32px' }}>
              <ListItem>
                <ListItemSecondaryAction className={classes.listAction}>
                  <IconButton
                    id={client.id}
                    edge="end"
                    onClick={(e) => {
                      setCurrentClient(client);
                      handleIconClick(e, client);
                    }}
                    aria-controls="simple-menu"
                    aria-haspopup="true"
                    size="large"
                  >
                    <MoreVertical />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </>
        );
      },
      valueGetter: ({ row }) => {
        return row.user.efxDigitalIdentityTrust;
      },
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarFilterButton
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...({} as any)
          }
        />
        <Grid container justifyContent="flex-end">
          <GridToolbarExport
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...({} as any)
            }
            csvOptions={{
              fields: ['fullName', 'email', 'createdAt', 'portal', 'estates'],
              fileName: `trustate-all-clients-${new Date().toISOString().slice(0, 10)}`,
              utf8WithBom: true,
            }}
            excelOptions={{
              fields: ['fullName', 'email', 'createdAt', 'portal', 'estates'],
              fileName: `trustate-all-clients-${new Date().toISOString().slice(0, 10)}`,
              allColumns: true,
            }}
          />
        </Grid>
      </GridToolbarContainer>
    );
  }

  function CustomFilterPanel() {
    return (
      <div className={classes.filterPanelWrapper}>
        <GridFilterPanel />
      </div>
    );
  }

  const estates = currentClient?.defaultPortal.estates;

  const popoverContent = estates ? (
    estates.map((estate: Estate) => {
      const createdAtFormatted = format(
        Date.parse(estate.createdAt.toString()),
        'h:mmaaa LLL d, y',
      );
      return (
        <div>
          {estate.estateName}{' '}
          <Typography variant="caption">created {createdAtFormatted}</Typography>
        </div>
      );
    })
  ) : (
    <></>
  );

  return (
    <>
      {auditEntityId ? (
        <AuditModal
          entityId={auditEntityId!}
          setShowModal={setShowAuditModal}
          showModal={showAuditModal}
        />
      ) : (
        ''
      )}
      {currentClient && (
        <Popover
          id={currentClient.id}
          open={userEstatesDetailAnchorEl?.id === currentClient.id}
          anchorEl={userEstatesDetailAnchorEl}
          onClose={handleUserEstatesDetailClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <div className={classes.popoverContent}>{popoverContent}</div>
        </Popover>
      )}
      {currentClient && (
        <Menu
          open={anchorEl?.id === currentClient.id}
          anchorEl={anchorEl}
          keepMounted
          onClose={() => handleMenuClick()}
        >
          <MenuItem
            key="impersonate"
            onClick={() => {
              handleMenuClick();
              handleImpersonation();
            }}
            disabled={currentClient.accountDisabled}
          >
            Impersonate
          </MenuItem>
          <MenuItem
            key="show audit"
            onClick={() => {
              handleMenuClick();
              setAuditEntityId(currentClient.id);
              setShowAuditModal(true);
            }}
          >
            Show audit trail
          </MenuItem>
          <MenuItem
            key="Request EFX DIT"
            onClick={() => {
              handleMenuClick();
              handleRequestEfxDit();
            }}
          >
            Request EFX DIT
          </MenuItem>
        </Menu>
      )}
      <Typography className={classes.title} variant="h5">
        All Clients
      </Typography>
      <div className={classes.usersContainer}>
        <div style={{ height: '600px', width: '100%' }}>
          <ServerEnabledDataGrid<PortalMembership_AllClientsDataGridPageFilter>
            className={classes.root}
            gridStateKey={GridStateKey.AllClientsGrid}
            columns={columns}
            queryResult={getAllPortalMembershipsForAllClientsDataGridLazyQueryResult}
            executeLazyQuery={getAllPortalMembershipsForAllClientsDataGridLazyQuery}
            disableColumnReorder
            disableRowSelectionOnClick
            autoHeight={true}
            rowHeight={65}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  email: false,
                },
              },
            }}
            slots={{
              toolbar: CustomToolbar,
              filterPanel: CustomFilterPanel,
            }}
          />
        </div>
      </div>
    </>
  );
}
